import React, { useEffect, useRef, useState } from 'react'
import styles from './Pallets.module.css'
import st from './Stock.module.css'
import o from '../../components/Overlay.module.css'
import sd from './SdPanel.module.css'
import { SdPanel } from './SdPanel'
import { apiJson } from '../../lib/api'
import { useLayer, useChanged } from '../../components/layers'
import { PendingButton } from '../../components/pending'
import { digits, fmtDay, plural } from './model'
import { differenceText, type StockTakePreview, type StockTakeScope } from './stock'

// Stock take (Adrian and Vanessa, 2026-11-02; migration 187): count full
// pallets and loose boxes on a stock line (from a summary row, every harvest
// date of it) or one source group (from a dated row), and the count replaces
// on hand. The API plans it (POST /api/stock/take/preview): a shortfall comes
// off the oldest harvest first, stock found goes onto the newest. The commit
// sends the on hand the person saw, and is refused if it has moved since.

const errText = (e: any) => e?.body?.error || e?.message || String(e)

export function StockTakePanel({
  user,
  sourceId,
  scope,
  title,
  onCancel,
  onDone,
}: {
  user: any
  sourceId: string
  scope: StockTakeScope
  title: string
  onCancel: () => void
  onDone: (message: string) => void
}) {
  const [pallets, setPallets] = useState('')
  const [loose, setLoose] = useState('')
  const [reason, setReason] = useState('')
  const [preview, setPreview] = useState<StockTakePreview | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const entered = pallets !== '' || loose !== ''
  const np = parseInt(pallets, 10) || 0
  const nl = parseInt(loose, 10) || 0
  const shown = preview && preview.counted_pallets === np && preview.loose_boxes === nl ? preview : null
  const ok = entered && !!shown && reason.trim().length > 0

  const userRef = useRef(user)
  userRef.current = user
  useEffect(() => {
    let live = true
    const t = setTimeout(
      () => {
        apiJson<StockTakePreview>(userRef.current, '/api/stock/take/preview', {
          method: 'POST',
          body: { source_id: sourceId, scope, counted_pallets: np, loose_boxes: nl },
        })
          .then((r) => {
            if (!live) return
            setPreview(r)
            setError(null)
          })
          .catch((e) => live && setError(errText(e)))
      },
      preview ? 250 : 0
    )
    return () => {
      live = false
      clearTimeout(t)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceId, scope, np, nl])

  const submit = async () => {
    if (!shown) return
    setBusy(true)
    try {
      const r = await apiJson<{ message: string }>(userRef.current, '/api/stock/take', {
        method: 'POST',
        body: { source_id: sourceId, scope, counted_pallets: np, loose_boxes: nl, reason: reason.trim(), expected_boxes: shown.system_boxes },
      })
      onDone(r.message)
    } catch (e) {
      setError(errText(e))
      setPreview(null)
      setBusy(false)
    }
  }

  const panelEl = useRef<HTMLDivElement>(null)
  const dirty = useChanged({ pallets, loose, reason })
  const panel = useLayer(true, onCancel, { dirty: dirty && !busy, el: panelEl, modal: true, busy })
  const cap = preview?.capacity

  return (
    <SdPanel
      title="Stock take"
      onCancel={panel.requestClose}
      confirm={panel.confirm}
      panelEl={panelEl}
      busy={busy}
      foot={
        <PendingButton type="button" className={o.primary} busy={busy} disabled={!ok} pending="Recording…" onClick={submit}>
          Record stock take
        </PendingButton>
      }
    >
      <div className={sd.text}>
        {title}
        {preview && (
          <div className={styles.sub}>
            {scope === 'line' ? plural(preview.groups, 'source group') + ' · ' : ''}
            {plural(preview.system_boxes, 'box', 'boxes')} on hand · {preview.capacity} per pallet
          </div>
        )}
      </div>
      <p className={sd.help}>
        Count what is physically there. The count replaces on hand{scope === 'line' ? ' for every harvest date on this line' : ''}: boxes missing come
        off the oldest harvest first, boxes found go onto the newest. Receipts and picking records are not changed.
      </p>
      <div className={sd.grid}>
        <label className={o.field}>
          <span className={o.fieldLabel}>Full pallets{cap ? ` (${cap} boxes)` : ''}</span>
          <input className={`${o.input} ${sd.num}`} inputMode="numeric" aria-label="Full pallets" value={pallets} onChange={(e) => setPallets(digits(e.target.value))} />
        </label>
        <label className={o.field}>
          <span className={o.fieldLabel}>Loose boxes</span>
          <input className={`${o.input} ${sd.num}`} inputMode="numeric" aria-label="Loose boxes" value={loose} onChange={(e) => setLoose(digits(e.target.value))} />
        </label>
        <label className={o.field}>
          <span className={o.fieldLabel}>Reason (required)</span>
          <input className={o.input} aria-label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
        </label>
      </div>
      {error && (
        <div className={styles.errList} role="alert">
          {error}
        </div>
      )}
      <div className={sd.inset} data-testid="stock-take-preview">
        <div className={st.impactTitle}>Count against stock</div>
        {!entered || !shown ? (
          <div className={styles.sub}>Enter the full pallets and loose boxes counted.</div>
        ) : (
          <>
            <div>
              Counted <strong>{plural(shown.counted_boxes, 'box', 'boxes')}</strong> · on hand {shown.system_boxes} ·{' '}
              <strong data-testid="stock-take-difference">{differenceText(shown.difference)}</strong>
            </div>
            {shown.changes.map((c) => (
              <div key={c.source_id} className={styles.sub}>
                {fmtDay(c.harvest_date)}
                {c.site_name ? ' · ' + c.site_name : ''}
                {c.block_name ? ' / ' + c.block_name : ''}: {c.before} → {c.after}
              </div>
            ))}
            {shown.lines.map((line) => (
              <div key={line}>• {line}</div>
            ))}
          </>
        )}
      </div>
    </SdPanel>
  )
}
